"use client";
import { useQuery } from "@tanstack/react-query";
import { Camera, FolderSearch, ScanLine, Search, Tag, X } from "lucide-react";
import { useRouter } from "next/navigation";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../app/providers";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAppStore } from "../lib/store";
import type { Product } from "../lib/types";

type Suggestions = {
  products: Product[];
  categories: { id: string; slug: string; name: string }[];
  brands: { id: string; slug: string; name: string }[];
};

type BarcodeDetectorLike = { detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]> };
type BarcodeDetectorConstructor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

export function SearchBar({ initial = "", large = false }: { initial?: string; large?: boolean }) {
  const [value, setValue] = useState(initial);
  const [debounced, setDebounced] = useState(initial);
  const [focused, setFocused] = useState(false);
  const [scanner, setScanner] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t, locale } = useI18n();
  const router = useRouter();
  const { toast } = useToast();
  const recent = useAppStore((state) => state.recentSearches);
  const addRecent = useAppStore((state) => state.addRecentSearch);
  const clearRecent = useAppStore((state) => state.clearRecentSearches);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), 260);
    return () => window.clearTimeout(timer);
  }, [value]);

  const suggestions = useQuery({
    queryKey: ["suggestions", debounced, locale],
    queryFn: () => api<Suggestions>(`/search/suggestions?q=${encodeURIComponent(debounced)}&locale=${locale}`),
    enabled: debounced.trim().length >= 2,
  });

  const submitValue = useCallback((queryValue: string) => {
    const query = queryValue.trim();
    if (!query) return;
    addRecent(query);
    setFocused(false);
    setScanner(false);
    router.push(`/search?q=${encodeURIComponent(query)}`);
  }, [addRecent, router]);
  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    submitValue(value);
  };

  useEffect(() => {
    if (!scanner) return;
    let stream: MediaStream | undefined;
    let timer = 0;
    let active = true;
    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (!active || !videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraReady(true);
        const Detector = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
        if (Detector) {
          const detector = new Detector({ formats: ["ean_13", "ean_8", "upc_a", "code_128"] });
          timer = window.setInterval(async () => {
            try {
              const codes = await detector.detect(videoRef.current!);
              if (codes[0]?.rawValue) submitValue(codes[0].rawValue);
            } catch { /* keep scanning */ }
          }, 450);
        }
      } catch {
        setCameraReady(false);
        toast(t("scannerUnavailable"), "error");
      }
    };
    void start();
    return () => {
      active = false;
      window.clearInterval(timer);
      stream?.getTracks().forEach((track) => track.stop());
      setCameraReady(false);
    };
  }, [scanner, submitValue, t, toast]);

  const hasPopover = focused && Boolean(suggestions.data?.products.length || suggestions.data?.categories.length || suggestions.data?.brands.length || (!debounced && recent.length));
  return <div className={`search-component ${large ? "large" : ""}`} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) window.setTimeout(() => setFocused(false), 80); }}>
    <form className="search" onSubmit={submit}>
      <Search size={22} />
      <input value={value} onChange={(event) => setValue(event.target.value)} onFocus={() => setFocused(true)} aria-label={t("searchPlaceholder")} placeholder={t("searchPlaceholder")} />
      {value && <button type="button" className="search-clear" onClick={() => setValue("")} aria-label={t("clear")}><X size={18} /></button>}
      <button type="submit" className="search-submit">{t("search")}</button>
    </form>
    <button className="scan" onClick={() => setScanner(true)} aria-label={t("scan")}><ScanLine size={20} /><span>{t("scan")}</span></button>
    {hasPopover && <div className="search-popover">
      {!!suggestions.data?.products.length && <div className="popover-head"><b>{t("productSuggestions")}</b></div>}
      {suggestions.data?.products.map((item) => <button key={item.id} onMouseDown={() => router.push(`/product/${item.slug}`)}><Search size={15} /><span>{item.name}</span><small>{item.brand.name}</small></button>)}
      {!!suggestions.data?.categories.length && <><div className="popover-head"><b>{t("categorySuggestions")}</b></div>{suggestions.data.categories.map((item) => <button key={item.id} onMouseDown={() => router.push(`/category/${item.slug}`)}><FolderSearch size={15} /><span>{item.name}</span></button>)}</>}
      {!!suggestions.data?.brands.length && <><div className="popover-head"><b>{t("brandSuggestions")}</b></div>{suggestions.data.brands.map((item) => <button key={item.id} onMouseDown={() => router.push(`/catalog?brand=${item.slug}`)}><Tag size={15} /><span>{item.name}</span></button>)}</>}
      {!debounced && recent.length > 0 && <><div className="popover-head"><b>{t("recentSearches")}</b><button onMouseDown={clearRecent}>{t("clear")}</button></div>{recent.map((item) => <button key={item} onMouseDown={() => { setValue(item); submitValue(item); }}><Search size={15} /><span>{item}</span></button>)}</>}
    </div>}
    {scanner && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setScanner(false); }}>
      <section className="scanner-sheet" role="dialog" aria-modal="true" aria-labelledby="scanner-title">
        <header><div><small>{t("scan")}</small><h2 id="scanner-title">{t("scannerTitle")}</h2></div><button onClick={() => setScanner(false)} aria-label={t("cancelAction")}><X /></button></header>
        <div className={`scanner-camera ${cameraReady ? "ready" : ""}`}><video ref={videoRef} muted playsInline /><span><Camera />{t("scannerHint")}</span><i /></div>
        <form onSubmit={submit} className="scanner-manual"><label>{t("enterBarcode")}<input inputMode="numeric" value={value} onChange={(event) => setValue(event.target.value)} placeholder="4870000123456" /></label><button className="primary-button" type="submit">{t("openProduct")}</button></form>
      </section>
    </div>}
  </div>;
}
